import { useRef } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Building2, KeyRound } from "lucide-react";
import { IMAGES } from "@/lib/constants";

/* Signature on-load reveal: each line is a clip-path mask that reveals from below with stagger */
const LINE_MASK = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({
    y: "0%",
    opacity: 1,
    transition: {
      duration: 1.05,
      delay: 0.15 + i * 0.11,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

function MaskedLine({ children, index = 0, className = "" }) {
  return (
    <span className="block overflow-hidden pb-[0.05em]">
      <motion.span
        custom={index}
        variants={LINE_MASK}
        initial="hidden"
        animate="show"
        className={`block will-change-transform ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Light parallax (translate only - no scale/opacity work per frame)
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", reduced ? "0%" : "-8%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end bg-[#0a0a0a]"
    >
      {/* Parallax layered image */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-[-10%_0] will-change-transform"
      >
        <img
          src={IMAGES.hero}
          alt="J.F. Kennedy Suiten - stue, Aalborg"
          className="w-full h-full object-cover"
        />
      </motion.div>
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/55 to-[#0a0a0a]/15 pointer-events-none" />
      {/* Static darkening overlay */}
      <div className="absolute inset-0 bg-[#0a0a0a]/40 pointer-events-none" />
      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-16 sm:pb-24 lg:pb-32 pt-28 sm:pt-32"
      >
        {/* Overline */}
        <motion.p
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.05, ease: "easeOut" }}
          className="overline text-[10px] sm:text-xs mb-5 sm:mb-7"
          style={{ color: "#D4AF37" }}
        >
          <span className="inline-block w-8 sm:w-12 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
          <span className="text-white/70 mr-2">01 —</span> Møblerede boliger · Hele Danmark
        </motion.p>

        {/* Signature masked headline */}
        <h1
          className="font-serif text-white text-[2.75rem] leading-[1.02] sm:text-[4.5rem] lg:text-[6.5rem] lg:leading-[0.98] font-light tracking-[-0.02em] max-w-5xl"
          style={{ fontFamily: "Fraunces, serif" }}
        >
          <MaskedLine index={0}>Find dit næste</MaskedLine>
          <MaskedLine index={1} className="italic text-[#D4AF37]">
            ophold.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: "easeOut" }}
          data-testid="hero-subtitle"
          className="mt-6 sm:mt-10 max-w-xl text-white/80 text-base sm:text-xl leading-relaxed font-light"
        >
          Møblerede boliger til korte og længere ophold.
        </motion.p>

        {/* Two primary entrances */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: "easeOut" }}
          className="mt-8 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-5"
        >
          <HashLink
            smooth
            to="/#boliger"
            data-testid="hero-find-bolig-btn"
            className="btn-gold group"
            style={{
              background: "#D4AF37",
              borderColor: "#D4AF37",
              color: "#111111",
            }}
          >
            <span className="inline-flex items-center gap-2.5">
              <KeyRound size={15} strokeWidth={1.6} />
              Find bolig
            </span>
          </HashLink>
          <Link
            to="/firmaboliger"
            data-testid="hero-for-virksomheder-btn"
            className="btn-outline-dark"
            style={{ borderColor: "#ffffff", color: "#ffffff" }}
          >
            <span className="inline-flex items-center gap-2.5">
              <Building2 size={15} strokeWidth={1.6} />
              For virksomheder
            </span>
          </Link>
        </motion.div>

        {/* Secondary CTA for owners */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-10 sm:mt-14 pt-6 sm:pt-8 border-t border-white/15 max-w-3xl flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
        >
          <span className="text-white/55 text-xs sm:text-sm font-light">
            Ejer du en bolig, du gerne vil have udlejet?
          </span>
          <HashLink
            smooth
            to="/#boligejere"
            data-testid="hero-udlej-bolig-link"
            className="group inline-flex items-center gap-2 text-[11px] sm:text-xs tracking-[0.2em] uppercase text-[#D4AF37] hover:text-white transition-colors duration-500"
          >
            <span>Udlej din bolig</span>
            <ArrowRight
              size={14}
              strokeWidth={1.5}
              className="transition-transform duration-500 group-hover:translate-x-1"
            />
          </HashLink>
        </motion.div>
      </motion.div>
    </section>
  );
}
